import React from 'react';
import { NodeApi, Tree } from 'react-arborist';
import {
    Box,
    Button,
    Checkbox,
    Divider,
    Flex,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Text,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { linkStyle, basicLinkStyle } from 'shared/styles';
import { IkeywordsByType } from 'types';
import { Flag } from 'react-feather';
import { useTranslation } from 'helpers/languages/translations';
import { TKeywordCategories } from './useKeywordCategories';

interface ICategoriesModal {
    updateInitialRef: React.MutableRefObject<null>;
    isOpen: boolean;
    treeOpenByDefault: boolean;
    setTreeOpenByDefault: React.Dispatch<React.SetStateAction<boolean>>;
    handleCloseAddAndUpdateModal: () => void;
    modalType: 'edit' | 'view';
    keywordFilter: string;
    setKeywordFilter: React.Dispatch<React.SetStateAction<string>>;
    keywordsToSelect: {
        priorityKeyword: boolean;
        nonPriorityKeyword: boolean;
    };
    setKeywordsToSelect: React.Dispatch<
        React.SetStateAction<{
            priorityKeyword: boolean;
            nonPriorityKeyword: boolean;
        }>
    >;
    setKeywordsByType: React.Dispatch<React.SetStateAction<IkeywordsByType[]>>;
    isUpdateCategoryLoading: boolean;
    handleSubmit: () => Promise<void>;
    filteredKeywords: IkeywordsByType[];
    visibleKeywords: number[];
    languages: TKeywordCategories['languages'];
}

const CategoriesModal: React.FC<ICategoriesModal> = ({
    updateInitialRef,
    isOpen,
    treeOpenByDefault,
    setTreeOpenByDefault,
    handleCloseAddAndUpdateModal,
    modalType,
    keywordFilter,
    setKeywordFilter,
    keywordsToSelect,
    setKeywordsToSelect,
    setKeywordsByType,
    isUpdateCategoryLoading,
    handleSubmit,
    filteredKeywords,
    visibleKeywords,
    languages,
}) => {
    const translate = useTranslation();
    const filterLinksStyles = {
        ...linkStyle,
        textDecoration: 'none',
        marginLeft: '10px',
    };

    const checkboxContainerStyles =
        modalType === 'edit'
            ? {
                  minWidth: '36px',
              }
            : {};
    const treeData =
        modalType === 'edit'
            ? filteredKeywords
            : filteredKeywords.map((e) => ({
                  ...e,
                  keywords: e.keywords.filter((keyword) => visibleKeywords.includes(Number(keyword.id))),
              }));

    return (
        <Modal initialFocusRef={updateInitialRef} isOpen={isOpen} onClose={handleCloseAddAndUpdateModal} size="2xl">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader tabIndex={-1} ref={updateInitialRef}>
                    {modalType === 'edit'
                        ? translate('categories_keywords.column.associate_keywords')
                        : translate('categories_keywords.column.keywords')}
                </ModalHeader>
                <ModalCloseButton mt="2" />
                <ModalBody>
                    <Box mb="15px">
                        <Input
                            placeholder={translate('placeholder.search') as string}
                            value={keywordFilter}
                            onChange={(e) => setKeywordFilter(e.target.value)}
                        />
                        {modalType === 'edit' && (
                            <Flex fontSize="sm" mt="22px">
                                <Text fontWeight="bold">{translate('categories_keywords.keywords.select')}</Text>
                                <Text
                                    {...filterLinksStyles}
                                    onClick={() => {
                                        setKeywordsToSelect({
                                            priorityKeyword: true,
                                            nonPriorityKeyword: true,
                                        });
                                    }}
                                    fontWeight={
                                        (keywordsToSelect.priorityKeyword && keywordsToSelect.nonPriorityKeyword) ||
                                        (!keywordsToSelect.priorityKeyword && !keywordsToSelect.nonPriorityKeyword)
                                            ? 'bold'
                                            : 'normal'
                                    }
                                >
                                    {translate('categories_keywords.keywords.all')}
                                </Text>
                                <Text
                                    {...filterLinksStyles}
                                    onClick={() => {
                                        setKeywordsToSelect({
                                            priorityKeyword: true,
                                            nonPriorityKeyword: false,
                                        });
                                    }}
                                    fontWeight={
                                        keywordsToSelect.priorityKeyword && !keywordsToSelect.nonPriorityKeyword
                                            ? 'bold'
                                            : 'normal'
                                    }
                                >
                                    {translate('categories_keywords.keywords.priority')}
                                </Text>
                                <Text
                                    {...filterLinksStyles}
                                    onClick={() => {
                                        setKeywordsToSelect({
                                            priorityKeyword: false,
                                            nonPriorityKeyword: true,
                                        });
                                    }}
                                    fontWeight={
                                        !keywordsToSelect.priorityKeyword && keywordsToSelect.nonPriorityKeyword
                                            ? 'bold'
                                            : 'normal'
                                    }
                                >
                                    {translate('categories_keywords.keywords.non_priority')}
                                </Text>
                            </Flex>
                        )}
                        {modalType === 'edit' && <Divider mt="20px" />}
                    </Box>
                    <Flex mb="20px" justifyContent="space-between" alignItems="center" fontSize="sm">
                        <Text {...basicLinkStyle} onClick={() => setTreeOpenByDefault(!treeOpenByDefault)}>
                            {translate('categories_keywords.modal.expand_all')} /{' '}
                            {translate('categories_keywords.modal.collapse_all')}
                        </Text>

                        {modalType === 'edit' && (
                            <Text
                                {...basicLinkStyle}
                                onClick={() => {
                                    const allTypesSelected = filteredKeywords.every((e) => e.checked);
                                    setKeywordsByType((prev) => {
                                        return prev.map((e, typeIndex) => {
                                            const newKeywords = e.keywords.map((keyword) => {
                                                const newKeyword = { ...keyword };
                                                if (
                                                    filteredKeywords[typeIndex].keywords.some(
                                                        (kw) => kw.id === newKeyword.id,
                                                    )
                                                ) {
                                                    newKeyword.checked = !allTypesSelected;
                                                }
                                                return newKeyword;
                                            });
                                            return { ...e, keywords: newKeywords, checked: !allTypesSelected };
                                        });
                                    });
                                }}
                            >
                                {translate('categories_keywords.modal.select_all')} /{' '}
                                {translate('categories_keywords.modal.deselect_all')}
                            </Text>
                        )}
                    </Flex>
                    <Tree
                        data={treeData}
                        childrenAccessor={'keywords'}
                        openByDefault={treeOpenByDefault}
                        key={treeOpenByDefault ? 'open' : 'close'}
                        width={'100%'}
                        height={300}
                        indent={30}
                        rowHeight={33}
                    >
                        {({ node, style }: { node: NodeApi<IkeywordsByType>; style: React.CSSProperties }) => {
                            const iconParams = {
                                onClick: () => {
                                    if (node.isOpen) {
                                        node.close();
                                    } else {
                                        node.open();
                                    }
                                },

                                style: {
                                    cursor: 'pointer',
                                    minWidth: '20px',
                                },
                            };

                            let NodeIcon;
                            if (!node.children || !node?.children?.length) {
                                NodeIcon = <Box minWidth="20px"></Box>;
                            } else if (node.isOpen) {
                                NodeIcon = <ChevronDownIcon {...iconParams} />;
                            } else {
                                NodeIcon = <ChevronRightIcon {...iconParams} />;
                            }
                            return (
                                <Flex style={style} alignItems="center">
                                    {NodeIcon}

                                    <Flex alignItems="center" {...checkboxContainerStyles}>
                                        {modalType === 'edit' &&
                                            ((node.children && node.data.keywords.length) || !node.data.keywords) && (
                                                <Checkbox
                                                    ml="10px"
                                                    mr="10px"
                                                    type="checkbox"
                                                    isChecked={
                                                        (!node.children && node.data.checked) ||
                                                        (node.data.keywords &&
                                                            node.data.keywords.some((keyword) => keyword.checked))
                                                    }
                                                    isIndeterminate={
                                                        node.data.keywords &&
                                                        node.data.keywords.some((keyword) => keyword.checked) &&
                                                        node.data.keywords.some((keyword) => !keyword.checked)
                                                    }
                                                    onChange={() => {
                                                        if (node.data.keywords) {
                                                            setKeywordsByType((prev) => {
                                                                const oldKeywordsByType = [...prev];
                                                                const indexOfOldKeywordsByType =
                                                                    oldKeywordsByType.findIndex(
                                                                        (e) => e.type === node.data.type,
                                                                    );
                                                                filteredKeywords.forEach((fkeyword) => {
                                                                    if (fkeyword.type === node.data.type) {
                                                                        fkeyword.keywords.forEach((fk) => {
                                                                            oldKeywordsByType[
                                                                                indexOfOldKeywordsByType
                                                                            ].keywords[fk.index].checked =
                                                                                !node.data.checked;
                                                                        });
                                                                        oldKeywordsByType[
                                                                            indexOfOldKeywordsByType
                                                                        ].checked = !node.data.checked;
                                                                    }
                                                                });

                                                                return oldKeywordsByType;
                                                            });
                                                        } else {
                                                            setKeywordsByType((prev) => {
                                                                return prev.map((e) => {
                                                                    if (e.type === node.data.type) {
                                                                        e.keywords = e.keywords.map((keyword) => {
                                                                            const newKeyword = { ...keyword };
                                                                            if (newKeyword.id === node.data.id) {
                                                                                newKeyword.checked = !node.data.checked;
                                                                            }
                                                                            return newKeyword;
                                                                        });
                                                                    }
                                                                    return e;
                                                                });
                                                            });
                                                        }
                                                    }}
                                                />
                                            )}
                                    </Flex>

                                    <Text fontSize={'sm'}>
                                        {node.data.name === 'default' ? (
                                            translate('categories_keywords.keywords.default')
                                        ) : (
                                            <Text as="span" position="relative">
                                                {node.data.name}
                                            </Text>
                                        )}
                                    </Text>
                                    {(node.data as any)?.master === 1 ? (
                                        <Box as="span" position="relative" ml="3px" fontSize="sm">
                                            {languages.length > 1 ? (
                                                <Text
                                                    as="span"
                                                    color="green"
                                                    fontWeight="bold"
                                                    position="absolute"
                                                    top="9px"
                                                    left="-10px"
                                                    fontSize="90%"
                                                >
                                                    {node.data.language ? `(${node.data.language})` : ''}
                                                </Text>
                                            ) : (
                                                <></>
                                            )}

                                            <Flag
                                                size={12}
                                                style={{ marginLeft: '5px' }}
                                                fill="#726E6E"
                                                color="#726E6E"
                                            />
                                        </Box>
                                    ) : (
                                        <Box as="span" ml="3px" fontSize="sm">
                                            {languages.length > 1 ? (
                                                <Text as="span" color="green" fontWeight="bold" fontSize="90%">
                                                    {node.data.language ? `(${node.data.language})` : ''}
                                                </Text>
                                            ) : (
                                                <></>
                                            )}
                                        </Box>
                                    )}
                                </Flex>
                            );
                        }}
                    </Tree>
                </ModalBody>
                <ModalFooter fontSize="sm" justifyContent="space-between">
                    <Flex flex="1" justifyContent="flex-end">
                        <Button size="sm" variant="outline" mr={3} onClick={handleCloseAddAndUpdateModal}>
                            {translate('keyword.modal.cancel')}
                        </Button>
                        <Button
                            isLoading={isUpdateCategoryLoading}
                            onClick={handleSubmit}
                            size="sm"
                            colorScheme="blue"
                            bg="blueMain"
                        >
                            {translate('keyword.modal.apply')}
                        </Button>
                    </Flex>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default CategoriesModal;
